import { Link } from "react-router-dom";
import type { GlowPost } from "@/lib/api";

interface ArticleRelatedProps {
  posts: GlowPost[];
  currentSlug: string;
}

export default function ArticleRelated({ posts, currentSlug }: ArticleRelatedProps) {
  const related = posts.filter((p) => p.slug !== currentSlug).slice(0, 3);

  if (related.length === 0) return null;

  return (
    <section
      className="border-t border-midnight/10 py-20 px-6 lg:px-16"
      style={{ fontFamily: "'Jost', sans-serif" }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-end justify-between mb-10">
          <div>
            <p
              className="text-[10px] tracking-widest uppercase text-midnight/35 mb-2"
              style={{ letterSpacing: "0.3em" }}
            >
              Editorial
            </p>
            <h2
              className="text-2xl md:text-3xl font-light italic text-midnight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              More from The Glow Edit
            </h2>
          </div>
          <Link
            to="/"
            className="text-xs tracking-widest uppercase text-midnight/50 hover:text-midnight transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
          >
            Back to Home <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {related.map((post) => (
            <Link
              key={post.id}
              to={`/editorial/${post.slug}`}
              className="group block cursor-pointer"
            >
              <div
                className="w-full overflow-hidden mb-4"
                style={{ aspectRatio: "16/11" }}
              >
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute" />
              </div>

              <span className="text-[9px] tracking-widest uppercase bg-midnight text-moonlight px-2.5 py-1">
                {post.tag}
              </span>

              <h3
                className="text-lg font-medium text-midnight group-hover:text-midnight/60 transition-colors leading-snug mt-3 mb-2"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {post.title}
              </h3>

              <p className="text-xs text-midnight/45 leading-relaxed line-clamp-2">
                {post.excerpt}
              </p>

              <span className="inline-flex items-center gap-1.5 mt-3 text-[10px] tracking-widest uppercase text-midnight/50 group-hover:text-midnight transition-colors">
                Read More
                <i className="ri-arrow-right-line text-xs transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
